import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import businessService from '../../../services/businessService';
import { store } from '../../../redux/store';
import { setSelectedBusiness } from '../../../redux/slices/applicationSlice';
import StepFlow, { StepFlowStep } from '../../components/misc/StepFlow';
import { SubscriptionType } from '../../../types/misc';
import { isSubscriptionGreaterThanOrEqual } from '../../../utils/subscriptionUtils';
import AreYouSureModal from '../../components/modals/AreYouSureModal';
import {
  Features,
  featuresInSubscription,
  getMinimumSubscriptionForFeatures,
} from '../../../utils/FeatureUtils';
import FeaturesForm from '../../components/misc/FeaturesForm';
import { SubscriptionSelector } from '../../components/selection-lists/SubscriptionSelector';

export default function ActivateDashboardPage() {
  const { business } = useLocation().state ?? {};
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState<SubscriptionType>(
    SubscriptionType.FREE,
  );
  const [features, setFeatures] = useState<Features>({});
  const indexState = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!business) navigate('/');
    try {
      // Check if features is already an object or needs to be parsed
      if (typeof business.features === 'string') {
        setFeatures(JSON.parse(business.features) as Features);
      } else if (business.features && typeof business.features === 'object') {
        setFeatures(business.features as Features);
      } else {
        setFeatures({});
      }
    } catch (e) {
      console.error('Failed to parse business.features:', e);
      setFeatures({});
    }
  }, []);

  if (!business) return <></>;

  const handleStep = (i: 1 | -1) => indexState[1](indexState[0] + i);

  const steps = [
    <FeaturesView
      setSelectedSubscriptionType={setSelectedSubscriptionType}
      features={features}
      setFeatures={setFeatures}
      setStep={handleStep}
    />,
    <SubscriptionStep
      setStep={handleStep}
      selectedSubscriptionType={selectedSubscriptionType}
      setSelectedSubscriptionType={setSelectedSubscriptionType}
      features={features}
      setFeatures={setFeatures}
    />,
  ];

  return <StepFlow indexState={indexState} steps={steps} />;
}

type FeaturesViewProps = {
  setSelectedSubscriptionType: (type: SubscriptionType) => void;
  features: Features;
  setFeatures: (features: Features) => void;
  setStep: (i: 1 | -1) => void;
};

function FeaturesView({
  setSelectedSubscriptionType,
  features,
  setFeatures,
  setStep,
}: FeaturesViewProps) {
  const { t } = useTranslation();
  const [warningOpen, setWarningOpen] = React.useState(false);
  const [deferredStepChange, setDeferredStepChange] = React.useState<null | 1 | -1>(null);
  const wrappedSetStep = (diff: 1 | -1) => {
    const anySelected = features && Object.values(features).some(Boolean);

    if (!anySelected && diff === 1) {
      setDeferredStepChange(diff);
      setWarningOpen(true);
    } else {
      setStep(diff);
    }
  };

  return (
    <>
      <StepFlowStep
        setStep={wrappedSetStep}
        showPrev={false}
        title={t('page.onboarding.businessFeaturesScreen.title')}
        subtitle={t('page.onboarding.businessFeaturesScreen.subtitle')}
        className="max-w-4xl">
        <FeaturesForm
          features={features}
          onFeaturesChange={(newFeature) => {
            const requiredSubscription = getMinimumSubscriptionForFeatures(newFeature);
            setFeatures(newFeature);
            setSelectedSubscriptionType(requiredSubscription);
          }}
        />
      </StepFlowStep>

      <AreYouSureModal
        open={warningOpen}
        setOpen={setWarningOpen}
        title={t('page.onboarding.businessFeaturesScreen.modal.title')}
        description={t('page.onboarding.businessFeaturesScreen.modal.description')}
        confirmLabel={t('page.onboarding.businessFeaturesScreen.modal.confirm')}
        cancelLabel={t('page.onboarding.businessFeaturesScreen.modal.cancel')}
        onConfirm={() => {
          setWarningOpen(false);
          if (deferredStepChange) {
            setStep(deferredStepChange);
            setDeferredStepChange(null);
          }
        }}
        onCancel={() => {
          setWarningOpen(false);
          setDeferredStepChange(null);
        }}
      />
    </>
  );
}

interface SubscriptionStepProps {
  setStep: (i: 1 | -1) => void;
  selectedSubscriptionType: SubscriptionType | null;
  setSelectedSubscriptionType: (type: SubscriptionType) => void;
  features: Features;
  setFeatures: (features: Features) => void;
}

function SubscriptionStep({
  setStep,
  selectedSubscriptionType,
  setSelectedSubscriptionType,
  features,
  setFeatures,
}: SubscriptionStepProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { business } = useLocation().state ?? {};
  const [downgradeWarningOpen, setDowngradeWarningOpen] = useState(false);

  const disableIncompatibleFeatures = () => {
    const allowedFeatures =
      featuresInSubscription[selectedSubscriptionType ?? SubscriptionType.FREE];
    const updatedFeatures = { ...features };
    Object.keys(updatedFeatures).forEach((key) => {
      if (!allowedFeatures[key as keyof typeof allowedFeatures]) {
        updatedFeatures[key as keyof typeof updatedFeatures] = false;
      }
    });
    return updatedFeatures;
  };

  const handleSubscriptionSelection = (subscriptionType: SubscriptionType) => {
    const requiredSubscription = getMinimumSubscriptionForFeatures(features || {});
    if (!isSubscriptionGreaterThanOrEqual(subscriptionType, requiredSubscription)) {
      setSelectedSubscriptionType(subscriptionType);
      setDowngradeWarningOpen(true);
    } else {
      setSelectedSubscriptionType(subscriptionType);
    }
  };

  const handleConfirmDowngrade = () => {
    if (selectedSubscriptionType) {
      const updatedFeatures = disableIncompatibleFeatures();
      setSelectedSubscriptionType(selectedSubscriptionType);
      setFeatures(updatedFeatures);
    }

    setDowngradeWarningOpen(false);
  };

  const handleCancelDowngrade = () => {
    setDowngradeWarningOpen(false);
  };

  const handleSubmit = async () => {
    await businessService.activateDashboard(
      business.businessId,
      features,
      selectedSubscriptionType || SubscriptionType.FREE,
    );
    store.dispatch(setSelectedBusiness(business.businessId));
    navigate('/overview');
  };

  return (
    <>
      <StepFlowStep
        setStep={setStep}
        disabled={!selectedSubscriptionType}
        showNext={false}
        onSubmit={handleSubmit}
        title={t('page.onboarding.subscription.title')}
        subtitle={t('page.onboarding.subscription.subtitle')}
        className="max-w-6xl">
        <SubscriptionSelector
          selectedSubscription={selectedSubscriptionType}
          mode="selectable"
          onSubscriptionSelect={handleSubscriptionSelection}
          showTrialPeriod
          trialDays={30}
        />
      </StepFlowStep>

      <AreYouSureModal
        open={downgradeWarningOpen}
        setOpen={setDowngradeWarningOpen}
        title={t('page.onboarding.subscription.downgrade.title')}
        description={t('page.onboarding.subscription.downgrade.description')}
        confirmLabel={t('page.onboarding.subscription.downgrade.confirm')}
        cancelLabel={t('page.onboarding.subscription.downgrade.cancel')}
        onConfirm={handleConfirmDowngrade}
        onCancel={handleCancelDowngrade}
      />
    </>
  );
}
